import React, { useEffect, useRef } from 'react';
import { LinkedDataRequestProject } from '../../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { Button } from 'reactstrap';
import './DataRequestProjectView.scss';
import createNotification from '../../../../utils/createNotification';
import { canUseDaqiDataTools } from '../../../../utils/daqiEditAccess';
import { InlineDaqiShell } from './InlineDaqiShell/InlineDaqiShell';

// Spike (N5-591): render the DAQI dashboard inline via InlineDaqiShell
// instead of embedding it in a same-origin iframe. Flip to false to fall
// back to the iframe path below, which is left fully intact.
const USE_INLINE_DAQI = true;

interface DataRequestProjectViewProps {
    party: TimelineParty;
    project: LinkedDataRequestProject;
    onCancel: () => void;
    questionnaireId: number;
    engagementId?: number;
    isReadOnly?: boolean;
}

export const DataRequestProjectView = (props: DataRequestProjectViewProps) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const isAdmin = props.party === TimelineParty.VetValue;
    const canUseDataTools = canUseDaqiDataTools(props.party);

    const buildDaqiQueryParams = () => {
        const params = new URLSearchParams({
            projectId: props.project.dataRequestProjectId.toString(),
            isAdmin: isAdmin.toString(),
            canUseDataTools: canUseDataTools.toString(),
            projectName: props.project.projectName,
            isExternalProject: 'true',
            questionnaireId: props.questionnaireId?.toString() || '',
            tab: props.questionnaireId ? '3' : '1',
            isReadOnly: (!!props.isReadOnly).toString(),
        });
        if (props.engagementId) {
            params.append('engagementId', props.engagementId.toString());
        }
        return params;
    };

    useEffect(() => {
        if (USE_INLINE_DAQI) {
            return;
        }

        const params = buildDaqiQueryParams();

        const iframe = document.getElementById(
            'data-request-frame'
        ) as HTMLIFrameElement;
        if (iframe) {
            iframe.src = `/data-request-dashboard-built-in?${params.toString()}`;
        }
    }, [props.project]);

    useEffect(() => {
        if (USE_INLINE_DAQI) {
            // The setHeight/PARENT_SCROLL/notification-relay bridge below only
            // makes sense when DAQI runs in a separate iframe document. Inline,
            // DAQI shares this window/document, so: its own toast() call already
            // renders once via the real ToastContainer (registering the listener
            // below would double it), its Rodal modals lock the real
            // document.body directly (useBodyScrollLock), and there is no
            // "parent" viewport to report height/scroll into.
            return;
        }

        const postScrollState = () => {
            const iframe = iframeRef.current;
            if (!iframe) {
                return;
            }
            const rect = iframe.getBoundingClientRect();
            // Document offset of the iframe top. rect.top and scrollY move in
            // opposite directions as the page scrolls, so this stays constant
            // while scrolling and only changes on resize/zoom — which keeps the
            // pop-up's height budget stable instead of resizing mid-scroll.
            const iframeDocTop = rect.top + window.scrollY;
            const viewportBudget = window.innerHeight - iframeDocTop - 24;
            // The pop-up sits inside the iframe, and the .rodal overlay is only
            // as tall as the iframe. Near the very bottom of the page that
            // overlay edge — not the viewport — becomes the binding limit, so
            // cap by the space left inside it to avoid a sliver overflow.
            const overlayBudget = rect.height - window.scrollY - 24;
            const maxDialogHeight = Math.max(
                240,
                Math.min(viewportBudget, overlayBudget)
            );
            iframe.contentWindow?.postMessage(
                {
                    type: 'PARENT_SCROLL',
                    scrollY: window.scrollY,
                    maxDialogHeight,
                },
                '*'
            );
        };

        const handleMessage = (e: MessageEvent) => {
            if (e.data?.type === 'setHeight' && iframeRef.current) {
                const minHeight =
                    window.innerHeight -
                    (iframeRef.current.getBoundingClientRect().top || 0) -
                    70;
                const newHeight = Math.max(e.data.height, minHeight);
                iframeRef.current.style.height = `${newHeight}px`;
            }
            if (e.data?.type === 'notification') {
                createNotification(
                    e.data.payload.message,
                    e.data.payload.notificationType,
                    true
                );
            }
            if (e.data?.type === 'REQUEST_SCROLL_STATE') {
                postScrollState();
            }
        };

        window.addEventListener('message', handleMessage);
        window.addEventListener('scroll', postScrollState);
        window.addEventListener('resize', postScrollState);
        postScrollState();

        return () => {
            window.removeEventListener('message', handleMessage);
            window.removeEventListener('scroll', postScrollState);
            window.removeEventListener('resize', postScrollState);
        };
    }, []);

    return (
        <div className="data-request-project-view">
            <div className="body_inner_content">
                <div className="obpbq_row valuation_section">
                    <div className="sampledetail">
                        <div className="mb-2 d-flex justify-content-between align-items-center">
                            <strong>{props.project.projectName}</strong>
                            <Button
                                color="primary"
                                onClick={() => props.onCancel()}>
                                Back
                            </Button>
                        </div>
                    </div>
                </div>
                {USE_INLINE_DAQI ? (
                    <InlineDaqiShell
                        key={props.project.dataRequestProjectId}
                        initialPath={`/data-request-dashboard-built-in?${buildDaqiQueryParams().toString()}`}
                        isAdmin={isAdmin}
                    />
                ) : (
                    <iframe id="data-request-frame" ref={iframeRef}></iframe>
                )}
            </div>
        </div>
    );
};
